/**
 * SSE (text/event-stream) chunk parser.
 *
 * Accepts UTF-8 chunks, buffers incomplete frames, and emits
 * completed event frames. Consumes blank-line delimiters between
 * frames. Individual event/data field parsing is left to the
 * consumer — this module is a pure text-level parser.
 */
export interface SseEvent {
  event: string;
  data: string;
}

export interface SseParseResult {
  events: SseEvent[];
  /** Remaining buffer to prepend to the next chunk. */
  remaining: string;
}

/**
 * Parse a raw text/event-stream chunk.
 *
 * Frames are separated by a double-newline (`\n\n`).
 * Each field line starts with `field:` followed by a value.
 * Unknown fields and comment lines (`:`) are silently ignored.
 *
 * Malformed frames (missing `event` or `data`) produce no event
 * — the raw text is discarded from the event stream.
 */
export function parseSseChunk(chunk: string, buffer: string): SseParseResult {
  const combined = buffer + chunk;
  const frames = combined.split('\n\n');
  const events: SseEvent[] = [];
  const remaining = frames.pop() ?? '';

  for (const frame of frames) {
    const event = parseFrame(frame);
    if (event) events.push(event);
  }

  return { events, remaining };
}

function parseFrame(frame: string): SseEvent | null {
  const lines = frame.split('\n');
  const fields: Record<string, string> = {};

  for (const line of lines) {
    // Comment lines — skip.
    if (line.startsWith(':')) continue;

    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).replace(/^ /, '');

    // First occurrence wins per SSE spec.
    if (key && !(key in fields)) {
      fields[key] = value;
    }
  }

  const event = fields.event || 'message';
  const data = fields.data ?? '';

  // A frame with no data and no explicit event is a keep-alive
  // or empty frame; skip it.
  if (!fields.data && fields.event === undefined) return null;

  return { event, data };
}
